import { logger } from '@/utils/logger';
import { apiClient } from '@/lib/api-client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegistrationData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  organization: string;
  consentPreferences?: { essential: boolean; version?: string };
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetSubmit {
  token: string;
  new_password: string;
  confirm_new_password: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  organization?: string;
  role: string;
  createdAt: string;
}

export const authApi = {
  register: async (data: RegistrationData) => {
    try {
      const response = await apiClient.post('/v1/auth/signup', {
        first_name: data.firstName,
        last_name: data.lastName,
        username: data.username,
        email: data.email,
        password: data.password,
        organization: data.organization,
        role: "user",
        ...(data.consentPreferences ? { consentPreferences: data.consentPreferences } : {})
      });
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; message?: string };
      logger.error('Registration failed:', err?.response?.status || err?.message);
      throw error;
    }
  },

  login: (credentials: LoginCredentials) =>
    apiClient.post('/v1/auth/login', credentials),

  forgotPassword: (data: PasswordResetRequest) =>
    apiClient.post('/v1/auth/password-reset-request', data),

  validateResetToken: (token: string) =>
    apiClient.get(`/v1/auth/password-reset-confirm/${token}`),

  resetPassword: (data: PasswordResetSubmit) =>
    apiClient.post(`/v1/auth/password-reset-confirm/${data.token}`, {
      new_password: data.new_password,
      confirm_new_password: data.confirm_new_password
    }),

  getProfile: () =>
    apiClient.get('/v1/auth/current-user'),

  updateProfile: (data: Partial<User>) =>
    apiClient.put('/v1/auth/profile', data),

  logout: async () => {
    try {
      await apiClient.get('/v1/auth/logout');
    } catch (error) {
      logger.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');

      localStorage.removeItem('user');
      localStorage.removeItem('user_profile');
      localStorage.removeItem('permissions');
      sessionStorage.clear();

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      window.location.replace('/login');
    }
  },

  forceLogout: () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/login');
  },

  resendVerification: (data: { email: string }) =>
    apiClient.post('/v1/auth/resend-verification', data),


  checkSessionExpiry: (): { willExpire: boolean; minutesLeft: number } => {
    const token = localStorage.getItem('auth_token');
    if (!token) return { willExpire: true, minutesLeft: 0 };

    try {
      const parts = token.split('.');
      const base64Payload = parts[1];
      const paddedPayload = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
      const payload = JSON.parse(atob(paddedPayload));

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;
      const minutesLeft = Math.floor(timeUntilExpiry / 60);

      return {
        willExpire: timeUntilExpiry < 300,
        minutesLeft: Math.max(0, minutesLeft)
      };
    } catch (error) {
      return { willExpire: true, minutesLeft: 0 };
    }
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        authApi.forceLogout();
        return false;
      }

      const base64Payload = parts[1];
      const paddedPayload = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
      const payload = JSON.parse(atob(paddedPayload));

      if (!payload.exp) {
        authApi.forceLogout();
        return false;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const isValid = currentTime < payload.exp - 60;

      if (!isValid) {
        authApi.forceLogout();
        return false;
      }

      return true;
    } catch (error) {
      authApi.forceLogout();
      return false;
    }
  },

  verifyToken: async (): Promise<boolean> => {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;

    try {
      if (!authApi.isAuthenticated()) {
        return false;
      }
      const response = await apiClient.get('/v1/auth/current-user');
      return !!response;
    } catch (error) {
      authApi.forceLogout();
      return false;
    }
  },

  getConsent: () => apiClient.get('/v1/auth/consent'),
  updateConsent: (payload: { essential?: boolean; version?: string }) =>
    apiClient.patch('/v1/auth/consent', payload),
};
